import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'

// We'll add CSS variables to global.css instead of injecting at runtime
// This approach works better with SSR and avoids flash of unstyled content

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer",
  {
    variants: {
      variant: {
        default:
          'bg-[var(--primary)] text-[var(--color-text-inverse)] shadow-xs hover:bg-[var(--primary)]/90',
        destructive:
          'bg-[var(--color-error)] text-white shadow-xs hover:bg-[var(--color-error)]/90 focus-visible:ring-[var(--color-error)]/20 dark:focus-visible:ring-[var(--color-error)]/40',
        outline:
          'border border-[var(--color-secondary)] bg-[var(--color-bg-primary)] shadow-xs hover:bg-[var(--color-secondary-light)] hover:text-[var(--color-text-inverse)]',
        secondary:
          'bg-[var(--color-secondary)] text-[var(--color-text-inverse)] shadow-xs hover:bg-[var(--color-secondary-dark)]',
        ghost:
          'hover:bg-[var(--color-secondary-light)] hover:text-[var(--color-text-primary)]',
        link: 'text-[var(--color-primary)] underline-offset-4 hover:underline',
      },
      size: {
        default: 'px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : 'button'

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
